import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ICreativeConversation } from '@packages/global';
import {
  PlusIcon,
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  EllipsisHorizontalIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface ConversationListProps {
  conversations: ICreativeConversation[];
  activeConversationId: string | null;
  loading: boolean;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string) => void;
}

/** 相对时间 */
function useRelativeTime(date: Date | string, t: (key: string, opts?: any) => string): string {
  const now = Date.now();
  const diff = now - new Date(date).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return t('time.justNow');
  if (minutes < 60) return t('time.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('time.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return t('time.daysAgo', { count: days });
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  activeConversationId,
  loading,
  onSelect,
  onCreate,
  onRename,
  onDelete,
}) => {
  const { t } = useTranslation('creative');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations;
    const q = searchQuery.toLowerCase();
    return conversations.filter((c) =>
      (c.title || '').toLowerCase().includes(q)
    );
  }, [conversations, searchQuery]);

  const startRename = (conv: ICreativeConversation) => {
    setEditingId(conv.id);
    setEditTitle(conv.title);
    setMenuOpenId(null);
  };

  const confirmRename = () => {
    if (editingId && editTitle.trim()) {
      onRename(editingId, editTitle.trim());
    }
    setEditingId(null);
  };

  const cancelRename = () => setEditingId(null);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') confirmRename();
    if (e.key === 'Escape') cancelRename();
  };

  const handleDelete = (id: string) => {
    setMenuOpenId(null);
    onDelete(id);
  };

  return (
    <div className="w-[280px] flex-shrink-0 border-r border-border-subtle bg-bg-form flex flex-col h-full">
      {/* Header: 标题 + 新建 + 搜索 */}
      <div className="px-5 pt-5 pb-4 flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-gray-50">{t('title')}</h2>
        <button
          onClick={onCreate}
          className="w-full flex items-center justify-center gap-2 h-10 rounded-lg
                     bg-primary hover:bg-primary/90 text-white text-sm font-medium
                     transition-colors cursor-pointer"
        >
          <PlusIcon className="w-[18px] h-[18px]" />
          {t('conversation.create')}
        </button>
        <div className="flex items-center gap-2 h-9 px-3 rounded-lg bg-bg-form border border-border-subtle">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('conversation.search')}
            className="flex-1 bg-transparent text-sm text-gray-50 placeholder-gray-500 focus:outline-none"
          />
        </div>
      </div>

      {/* 会话列表 */}
      <div className="flex-1 overflow-y-auto px-2">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <ChatBubbleLeftRightIcon className="w-10 h-10 text-gray-500 mb-3" />
            <p className="text-sm text-gray-400">{t('conversation.empty')}</p>
          </div>
        ) : (
          filteredConversations.map((conv) => {
            const isActive = conv.id === activeConversationId;
            const isEditing = conv.id === editingId;
            const isMenuOpen = conv.id === menuOpenId;

            return (
              <div
                key={conv.id}
                onClick={() => !isEditing && onSelect(conv.id)}
                className={`group relative flex items-center gap-3 px-3 py-3 my-0.5 rounded-lg transition-colors
                  ${isActive ? 'bg-bg-elevated' : 'hover:bg-bg-elevated/50'}
                  ${isEditing ? '' : 'cursor-pointer'}`}
              >
                {isEditing ? (
                  <div className="flex-1 flex items-center gap-1">
                    <input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onKeyDown={handleKeyDown}
                      autoFocus
                      className="flex-1 bg-bg-form border border-border-subtle rounded px-2 py-1 text-sm text-gray-50 focus:outline-none focus:border-border-default"
                    />
                    <button onClick={confirmRename} className="p-1 text-success hover:text-success/80 cursor-pointer">
                      <CheckIcon className="w-4 h-4" />
                    </button>
                    <button onClick={cancelRename} className="p-1 text-gray-400 hover:text-gray-300 cursor-pointer">
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <ChatBubbleLeftRightIcon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? 'text-primary' : 'text-gray-500'}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-[13px] truncate ${isActive ? 'text-gray-50 font-medium' : 'text-gray-300'}`}>
                        {conv.title || t('conversation.untitled')}
                      </p>
                      <p className="text-[11px] text-gray-500 mt-1">
                        <RelativeTime date={conv.updatedAt} t={t} />
                      </p>
                    </div>
                    {/* 三点菜单 */}
                    <div className="relative" ref={isMenuOpen ? menuRef : undefined}>
                      <button
                        onClick={(e) => { e.stopPropagation(); setMenuOpenId(isMenuOpen ? null : conv.id); }}
                        className={`p-1 text-gray-500 hover:text-gray-300 transition-opacity cursor-pointer
                          ${isMenuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                        aria-label="more"
                      >
                        <EllipsisHorizontalIcon className="w-4 h-4" />
                      </button>
                      {isMenuOpen && (
                        <div className="absolute right-0 top-full mt-1 w-32 bg-bg-elevated border border-border-subtle rounded-lg shadow-lg z-50 py-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); startRename(conv); }}
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-300 hover:bg-bg-surface transition-colors cursor-pointer"
                          >
                            <PencilIcon className="w-3.5 h-3.5" />
                            {t('conversation.rename')}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(conv.id); }}
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-error hover:bg-bg-surface transition-colors cursor-pointer"
                          >
                            <TrashIcon className="w-3.5 h-3.5" />
                            {t('conversation.delete')}
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

/** 相对时间组件 */
const RelativeTime: React.FC<{ date: Date | string; t: (key: string, opts?: any) => string }> = ({ date, t }) => {
  return <>{useRelativeTime(date, t)}</>;
};

export default ConversationList;
